import { getRequestCredentials, request } from "@prismicio/editor-support/Http"
import { queryOptions, useQueryClient } from "@tanstack/react-query"
import { z } from "zod"

import type { AuthStrategy } from "../../EditorConfig"

export const LocaleId = z.string()

export const BasicRole = z.enum(["Manager", "Writer", "Readonly"])

export const RolesPerLocale = z.record(LocaleId, BasicRole)
export type RolesPerLocale = z.infer<typeof RolesPerLocale>

export const RolesPerLocaleByUser = z.record(z.string(), RolesPerLocale)
export type RolesPerLocaleByUser = z.infer<typeof RolesPerLocaleByUser>

export const TeamSpace = z.object({
  id: z.string(),
  name: z.string(),
  customTypes: z.array(z.string()),
  rolesPerLocale: RolesPerLocale.optional(),
  rolesPerLocaleByUser: RolesPerLocaleByUser.optional(),
})

export type TeamSpace = z.infer<typeof TeamSpace>

const TeamSpaceUpsert = z.object({
  name: z.string(),
  customTypes: z.array(z.string()),
  rolesPerLocale: RolesPerLocale.optional(),
  rolesPerLocaleByUser: RolesPerLocaleByUser.optional(),
})

export type TeamSpaceUpsert = z.infer<typeof TeamSpaceUpsert>

const TeamSpacesResponse = z.object({ results: z.array(TeamSpace) })

export type TeamSpacesResponse = z.infer<typeof TeamSpacesResponse>

interface GetTeamSpacesArgs {
  baseUrl: URL
  repository: string
  authStrategy: AuthStrategy
}

export function getTeamSpaces(
  args: GetTeamSpacesArgs,
): Promise<TeamSpacesResponse> {
  const { baseUrl, repository, authStrategy } = args

  const url = new URL("team-spaces", baseUrl)
  url.searchParams.append("repository", repository)

  return request(url, {
    method: "GET",
    credentials: getRequestCredentials(authStrategy),
    okType: TeamSpacesResponse,
  })
}

export function getTeamSpacesOptions(args: GetTeamSpacesArgs) {
  return queryOptions({
    queryKey: ["teamSpaces", args],
    queryFn: () => getTeamSpaces(args),
    select: (data) => data.results,
  })
}

export function useInvalidateTeamSpaces() {
  const queryClient = useQueryClient()
  return () => queryClient.invalidateQueries({ queryKey: ["teamSpaces"] })
}

interface CreateTeamSpaceArgs {
  baseUrl: URL
  repository: string
  authStrategy: AuthStrategy
  payload: TeamSpaceUpsert
}

export function createTeamSpace(args: CreateTeamSpaceArgs): Promise<TeamSpace> {
  const { baseUrl, repository, authStrategy, payload } = args

  const url = new URL("team-spaces", baseUrl)
  url.searchParams.append("repository", repository)
  return request(url, {
    method: "POST",
    body: payload,
    credentials: getRequestCredentials(authStrategy),
    okType: TeamSpace,
  })
}

interface UpdateTeamSpaceArgs {
  baseUrl: URL
  repository: string
  authStrategy: AuthStrategy
  payload: TeamSpaceUpsert
  teamSpaceId: string
}

export function updateTeamSpace(args: UpdateTeamSpaceArgs): Promise<TeamSpace> {
  const { baseUrl, repository, authStrategy, payload, teamSpaceId } = args

  const url = new URL(`team-spaces/${teamSpaceId}`, baseUrl)
  url.searchParams.append("repository", repository)
  return request(url, {
    method: "PUT",
    body: payload,
    credentials: getRequestCredentials(authStrategy),
    okType: TeamSpace,
  })
}

interface DeleteTeamSpaceArgs {
  baseUrl: URL
  repository: string
  authStrategy: AuthStrategy
  teamSpaceId: string
}

export function deleteTeamSpace(args: DeleteTeamSpaceArgs): Promise<unknown> {
  const { baseUrl, repository, authStrategy, teamSpaceId } = args

  const url = new URL(`team-spaces/${teamSpaceId}`, baseUrl)
  url.searchParams.append("repository", repository)
  return request(url, {
    method: "DELETE",
    credentials: getRequestCredentials(authStrategy),
    okType: z.unknown(),
  })
}
